/**
 * PATH: apps/quote/src/pages/SupplierQuotesPage.tsx
 * PURPOSE: Manage and process supplier quotes - connected to backend APIs with AI extraction
 * 
 * FLOW:
 *   ┌─────────────┐    ┌─────────────┐    ┌─────────────┐    ┌─────────────┐
 *   │ Upload      │───▶│ AI Extract  │───▶│ Review      │───▶│ Apply to    │
 *   │ Quote PDF   │    │ Lines       │    │ Mapping     │    │ Pricebook   │
 *   └─────────────┘    └─────────────┘    └─────────────┘    └─────────────┘
 * 
 * AI EXTRACTION:
 *   Uses DeepSeek AI to parse PDFs/Excel files and extract structured line items
 *   with product codes, descriptions, quantities, and prices. Auto-maps to
 *   existing products where possible.
 * 
 * DEPENDENCIES:
 *   - quoteApi: API client for backend communication
 *   - @tanstack/react-query: Data fetching and caching
 */

import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Upload, FileSpreadsheet, CheckCircle, Clock, AlertCircle, 
  Eye, Trash2, Loader2, RefreshCw, Cpu, Sparkles
} from 'lucide-react';
import { quoteApi, type SupplierQuoteListItem, type ExtractionStatus } from '@/lib/quoteApi';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import { toast } from 'react-hot-toast';

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200', icon: Clock },
  processing: { label: 'Processing', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200', icon: Loader2 },
  extracted: { label: 'Extracted', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', icon: CheckCircle },
  reviewed: { label: 'Reviewed', color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200', icon: CheckCircle },
  applied: { label: 'Applied', color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200', icon: CheckCircle },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', icon: AlertCircle },
};

export default function SupplierQuotesPage() {
  const queryClient = useQueryClient();
  const [dragActive, setDragActive] = useState(false);
  const [extractingQuotes, setExtractingQuotes] = useState<Set<string>>(new Set());

  // Fetch supplier quotes
  const { 
    data: quotes = [], 
    isLoading,
    error,
  } = useQuery({
    queryKey: ['supplier-quotes'],
    queryFn: () => quoteApi.listSupplierQuotes(),
    // Auto-refresh every 5 seconds if we have processing quotes
    refetchInterval: (query) => {
      try {
        const data = query.state.data;
        // Defensive check: ensure data is an array before calling .some()
        if (!data || !Array.isArray(data) || data.length === 0) return false;
        const hasProcessing = data.some(q => 
          q && typeof q === 'object' && (q.status === 'processing' || extractingQuotes.has(q.id))
        );
        return hasProcessing ? 5000 : false;
      } catch {
        // If any error occurs, disable auto-refresh
        return false;
      }
    },
  });

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: (file: File) => quoteApi.uploadSupplierQuote(file),
    onSuccess: (data) => {
      toast.success('Quote uploaded successfully!');
      queryClient.invalidateQueries({ queryKey: ['supplier-quotes'] });
      // Auto-trigger extraction
      if (data.id) {
        extractMutation.mutate(data.id);
      }
    },
    onError: (error: Error) => {
      toast.error(`Upload failed: ${error.message}`);
    },
  });

  // Extract mutation
  const extractMutation = useMutation({
    mutationFn: (id: string) => quoteApi.extractSupplierQuote(id, false), // async mode
    onMutate: (id) => {
      setExtractingQuotes(prev => new Set(prev).add(id));
    },
    onSuccess: (data, id) => {
      toast.success('AI extraction started! Processing will complete shortly.');
      queryClient.invalidateQueries({ queryKey: ['supplier-quotes'] });
    },
    onError: (error: Error, id) => {
      setExtractingQuotes(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      toast.error(`Extraction failed: ${error.message}`);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => quoteApi.deleteSupplierQuote(id),
    onSuccess: () => {
      toast.success('Quote deleted');
      queryClient.invalidateQueries({ queryKey: ['supplier-quotes'] });
    },
    onError: (error: Error) => {
      toast.error(`Delete failed: ${error.message}`);
    },
  });

  // Clear extracting status when quotes finish processing
  useEffect(() => {
    if (quotes.length > 0) {
      setExtractingQuotes(prev => {
        const next = new Set(prev);
        quotes.forEach(q => {
          if (q.status !== 'processing' && q.status !== 'pending') {
            next.delete(q.id);
          }
        });
        return next;
      });
    }
  }, [quotes]);

  // Handle file drop
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    const validFile = files.find(f => 
      f.type === 'application/pdf' || 
      f.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      f.type === 'application/vnd.ms-excel'
    );
    
    if (validFile) {
      uploadMutation.mutate(validFile);
    }
  }, [uploadMutation]);

  // Handle file input
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadMutation.mutate(file);
    }
  };

  // Calculate stats
  const stats = {
    total: quotes.length,
    processed: quotes.filter(q => ['extracted', 'reviewed', 'applied'].includes(q.status)).length,
    pending: quotes.filter(q => q.status === 'pending').length,
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Error state - graceful degradation
  if (error) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Supplier Quotes</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Upload and process supplier quotes with AI extraction</p>
          </div>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
            <div>
              <h3 className="font-medium text-red-800 dark:text-red-200">Failed to load supplier quotes</h3>
              <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                {error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.'}
              </p>
            </div>
          </div>
          <button 
            onClick={() => queryClient.invalidateQueries({ queryKey: ['supplier-quotes'] })}
            className="mt-3 px-4 py-2 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded-md hover:bg-red-200 dark:hover:bg-red-900/60 transition-colors flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" /> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Supplier Quotes</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Upload and process supplier pricelists with AI extraction
          </p>
        </div>
        <label className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer">
          <Upload className="h-4 w-4" />
          Upload Quote
          <input
            type="file"
            accept=".pdf,.xlsx,.xls"
            onChange={handleFileChange}
            className="hidden"
          />
        </label>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
              <FileSpreadsheet className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Uploads</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-green-100 dark:bg-green-900 flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Processed</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.processed}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-yellow-100 dark:bg-yellow-900 flex items-center justify-center">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Pending</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.pending}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Drop Zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
        className={cn(
          "border-2 border-dashed rounded-xl p-8 text-center transition-colors",
          dragActive 
            ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" 
            : "border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50",
          uploadMutation.isPending && "opacity-50 pointer-events-none"
        )}
      >
        {uploadMutation.isPending ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <p className="text-gray-600 dark:text-gray-400">Uploading...</p>
          </div>
        ) : (
          <>
            <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              Drag and drop supplier quotes here
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              Supports PDF and Excel files (.pdf, .xlsx, .xls)
            </p>
          </>
        )}
      </div>

      {/* Error State */}
      {uploadMutation.isError && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">
            Failed to upload quote. Please try again.
          </p>
        </div>
      )}

      {/* Empty State */}
      {quotes.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-8 text-center">
          <Cpu className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No supplier quotes yet
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Upload supplier pricelists to extract prices using AI and update your pricebook automatically.
          </p>
        </div>
      )}

      {/* Quotes Table */}
      {quotes.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Supplier</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Reference</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Date</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">Items</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Status</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">Value</th>
                <th className="px-6 py-3 w-32"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {quotes.map((quote) => {
                const status = statusConfig[quote.status as keyof typeof statusConfig] || statusConfig.pending;
                const StatusIcon = status.icon;
                const totalValue = typeof quote.total === 'string' ? parseFloat(quote.total) : quote.total;

                return (
                  <tr key={quote.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">
                      {quote.supplier_name || 'Unknown Supplier'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FileSpreadsheet className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-900 dark:text-white">
                          {quote.quote_reference || 'No reference'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600 dark:text-gray-400">
                      {quote.quote_date ? formatDate(quote.quote_date) : formatDate(quote.created_at)}
                    </td>
                    <td className="px-6 py-4 text-right text-gray-600 dark:text-gray-400">
                      {quote.line_count > 0 ? quote.line_count : '—'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={cn(
                        'px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-fit',
                        status.color
                      )}>
                        <StatusIcon className={cn("h-3 w-3", quote.status === 'processing' && "animate-spin")} />
                        {status.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right font-medium text-gray-900 dark:text-white">
                      {totalValue > 0 ? formatCurrency(totalValue) : '—'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        <button 
                          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg"
                          title="View details"
                        >
                          <Eye className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                        </button>
                        {(quote.status === 'pending' || quote.status === 'failed') && (
                          <button 
                            onClick={() => extractMutation.mutate(quote.id)}
                            disabled={extractMutation.isPending || extractingQuotes.has(quote.id)}
                            className="px-3 py-1.5 text-sm bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:from-blue-600 hover:to-purple-600 disabled:opacity-50 flex items-center gap-1.5"
                            title="Extract with AI"
                          >
                            <Sparkles className="h-4 w-4" />
                            {quote.status === 'failed' ? 'Retry' : 'Extract'}
                          </button>
                        )}
                        {quote.status === 'processing' && (
                          <div className="px-3 py-1.5 text-sm bg-blue-50 dark:bg-blue-900/30 text-blue-600 rounded-lg flex items-center gap-1.5">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Extracting...
                          </div>
                        )}
                        {quote.status === 'extracted' && (
                          <button 
                            className="px-3 py-1.5 text-sm bg-green-50 dark:bg-green-900/30 text-green-600 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/50 flex items-center gap-1.5"
                            title="Review extracted lines"
                          >
                            <CheckCircle className="h-4 w-4" />
                            Review
                          </button>
                        )}
                        <button 
                          onClick={() => {
                            if (confirm('Delete this supplier quote?')) {
                              deleteMutation.mutate(quote.id);
                            }
                          }}
                          disabled={deleteMutation.isPending || quote.status === 'processing'}
                          className="p-2 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg disabled:opacity-50"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
